/* eslint-disable indent */
import React from 'react'
import { Table, Spinner } from 'react-bootstrap'

const simulations = 10000

const perMatch = value => (Number(value || 0) / simulations).toFixed(2)

const eventValue = (events, side, type) => {
  const suffix = side === 'home' ? 'Home' : 'Away'
  return Number(events?.[`total${type}${suffix}Goals`] || 0) +
    Number(events?.[`total${type}${suffix}Misses`] || 0)
}

const eventGoals = (events, side, type) =>
  Number(events?.[`total${type}${side === 'home' ? 'Home' : 'Away'}Goals`] || 0)

const Results = ({ simulateResults }) => {
  if (simulateResults === undefined) return null

  if (!simulateResults || typeof simulateResults !== 'object') {
    if (typeof simulateResults === 'string' && simulateResults !== 'No results') {
      return <div>{simulateResults}</div>
    }
    if (simulateResults === 'No results') return <div>No results</div>
    return (
      <div style={{ color: '#aaa' }}>
        <Spinner animation="border" />
      </div>
    )
  }

  const breakdown = simulateResults.chanceBreakdown
  const events = simulateResults.specialEvents
  const specialColumns = events
    ? [
        { label: 'Quick', type: 'Quick' },
        { label: 'Unpred', type: 'Unpred' },
        { label: 'Corner Anyone', type: 'CornerToAnyone' },
        { label: 'Corner Head', type: 'CornerToHead' },
        { label: 'Wing Anyone', type: 'WingerToAnyone' },
        { label: 'Wing Head', type: 'WingerToHead' },
        { label: 'Tech Head', type: 'TechnicalVsHead' },
      ]
    : []
  const specialTotal = side => specialColumns.reduce(
    (total, column) => total + eventValue(events, side, column.type),
    0
  )
  const specialGoalTotal = side => specialColumns.reduce(
    (total, column) => total + eventGoals(events, side, column.type),
    0
  )
  const standardColumns = breakdown
    ? [
        { label: 'Normal', chances: side => breakdown[side].left + breakdown[side].center + breakdown[side].right, goals: side => breakdown[side].leftGoals + breakdown[side].centerGoals + breakdown[side].rightGoals },
        { label: 'Left', chances: side => breakdown[side].left, goals: side => breakdown[side].leftGoals },
        { label: 'Center', chances: side => breakdown[side].center, goals: side => breakdown[side].centerGoals },
        { label: 'Right', chances: side => breakdown[side].right, goals: side => breakdown[side].rightGoals },
        { label: 'IFK', chances: side => breakdown[side].indirect, goals: side => breakdown[side].indirectGoals },
        { label: 'PK', chances: () => 0, goals: () => 0 },
        { label: 'DFK', chances: () => 0, goals: () => 0 },
        { label: 'LS', chances: side => eventValue(events, side, 'LongShot'), goals: side => eventGoals(events, side, 'LongShot') },
        { label: 'CA', chances: side => breakdown[side].counter, goals: side => breakdown[side].counterGoals },
        { label: 'SE', chances: specialTotal, goals: specialGoalTotal },
        { label: 'PNF', chances: side => breakdown[side].pnf, goals: side => breakdown[side].pnfGoals },
        { label: 'PDIM', chances: side => -breakdown[side].pdimReduced, goals: () => 0 },
      ]
    : []
  const standardTotal = side => standardColumns
    .filter(column => !['Normal', 'PDIM'].includes(column.label))
    .reduce((total, column) => total + column.chances(side), 0)
  const standardGoalTotal = side => standardColumns
    .filter(column => !['Normal', 'PDIM'].includes(column.label))
    .reduce((total, column) => total + column.goals(side), 0)
  const breakdownRow = (label, side, getter) => (
    <tr key={`${label}-${side}`}>
      <td>{label} {side === 'home' ? 'Home' : 'Away'}</td>
      <td>{getter({ chances: standardTotal, goals: standardGoalTotal })}</td>
      {standardColumns.map(column => <td key={column.label}>{getter(column)}</td>)}
    </tr>
  )

  return (
    <div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 'bold',
          marginBottom: 8,
        }}
      >
        90 min: {simulateResults.homeWins} - {simulateResults.draws} -{' '}
        {simulateResults.awayWins}
      </div>
      {simulateResults.extraTime && (
        <div style={{ marginBottom: 8 }}>
          120 min: {simulateResults.extraTime.homeWins} -{' '}
          {simulateResults.extraTime.draws} -{' '}
          {simulateResults.extraTime.awayWins}
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <span style={{ marginRight: 16 }}>
          HomeTeam avg. (90 min):{' '}
          <b>{Number(simulateResults.avgHomeGoals).toFixed(2)}</b>
        </span>
        <span>
          AwayTeam avg. (90 min):{' '}
          <b>{Number(simulateResults.avgAwayGoals).toFixed(2)}</b>
        </span>
      </div>
      <Table
        striped
        bordered
        hover
        variant="dark"
        size="sm"
        style={{ maxWidth: 400 }}
      >
        <thead>
          <tr>
            <th>Goals</th>
            <th>Home</th>
            <th>Away</th>
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2, 3, 4].map(i => (
            <tr key={i}>
              <td>{i}</td>
              <td>
                {simulateResults.homeGoalsArr &&
                simulateResults.homeGoalsArr[i] !== undefined
                  ? simulateResults.homeGoalsArr[i]
                  : 0}
              </td>
              <td>
                {simulateResults.awayGoalsArr &&
                simulateResults.awayGoalsArr[i] !== undefined
                  ? simulateResults.awayGoalsArr[i]
                  : 0}
              </td>
            </tr>
          ))}
          <tr>
            <td>5+</td>
            <td>
              {simulateResults.homeGoalsArr
                ? simulateResults.homeGoalsArr
                    .slice(5)
                    .reduce((a, b) => a + b, 0)
                : 0}
            </td>
            <td>
              {simulateResults.awayGoalsArr
                ? simulateResults.awayGoalsArr
                    .slice(5)
                    .reduce((a, b) => a + b, 0)
                : 0}
            </td>
          </tr>
        </tbody>
      </Table>
      {false && simulateResults.specialEvents && (
        <div style={{ marginTop: 16 }}>
          <h6>Special Events</h6>
          <Table
            striped
            bordered
            hover
            variant="dark"
            size="sm"
            style={{ maxWidth: 400 }}
          >
            <tbody>
              {/* Quick */}
              <tr>
                <td>Event</td>
                <td>Goals / Total</td>
              </tr>
              {'totalQuickHomeGoals' in simulateResults.specialEvents &&
                'totalQuickHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Quick (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalQuickHomeGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents.totalQuickHomeGoals +
                          simulateResults.specialEvents.totalQuickHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalQuickAwayGoals' in simulateResults.specialEvents &&
                'totalQuickAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Quick (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalQuickAwayGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents.totalQuickAwayGoals +
                          simulateResults.specialEvents.totalQuickAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {/* Unpred */}
              {'totalUnpredHomeGoals' in simulateResults.specialEvents &&
                'totalUnpredHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Unpred (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalUnpredHomeGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents.totalUnpredHomeGoals +
                          simulateResults.specialEvents.totalUnpredHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalUnpredAwayGoals' in simulateResults.specialEvents &&
                'totalUnpredAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Unpred (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalUnpredAwayGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents.totalUnpredAwayGoals +
                          simulateResults.specialEvents.totalUnpredAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalCornerToHeadHomeGoals' in simulateResults.specialEvents &&
                'totalCornerToHeadHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Corner to Head (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalCornerToHeadHomeGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents.totalCornerToHeadHomeGoals +
                          simulateResults.specialEvents
                            .totalCornerToHeadHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalCornerToHeadAwayGoals' in simulateResults.specialEvents &&
                'totalCornerToHeadAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Corner to Head (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalCornerToHeadAwayGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents.totalCornerToHeadAwayGoals +
                          simulateResults.specialEvents
                            .totalCornerToHeadAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalCornerToAnyoneHomeGoals' in simulateResults.specialEvents &&
                'totalCornerToAnyoneHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Corner to Anyone (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents
                          .totalCornerToAnyoneHomeGoals / 10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalCornerToAnyoneHomeGoals +
                          simulateResults.specialEvents
                            .totalCornerToAnyoneHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalCornerToAnyoneAwayGoals' in simulateResults.specialEvents &&
                'totalCornerToAnyoneAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Corner to Anyone (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents
                          .totalCornerToAnyoneAwayGoals / 10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalCornerToAnyoneAwayGoals +
                          simulateResults.specialEvents
                            .totalCornerToAnyoneAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalWingerToAnyoneHomeGoals' in simulateResults.specialEvents &&
                'totalWingerToAnyoneHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Winger to Anyone (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents
                          .totalWingerToAnyoneHomeGoals / 10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalWingerToAnyoneHomeGoals +
                          simulateResults.specialEvents
                            .totalWingerToAnyoneHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalWingerToAnyoneAwayGoals' in simulateResults.specialEvents &&
                'totalWingerToAnyoneAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Winger to Anyone (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents
                          .totalWingerToAnyoneAwayGoals / 10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalWingerToAnyoneAwayGoals +
                          simulateResults.specialEvents
                            .totalWingerToAnyoneAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalWingerToHeadHomeGoals' in simulateResults.specialEvents &&
                'totalWingerToHeadHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Winger to Head (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalWingerToHeadHomeGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalWingerToHeadHomeGoals +
                          simulateResults.specialEvents
                            .totalWingerToHeadHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalWingerToHeadAwayGoals' in simulateResults.specialEvents &&
                'totalWingerToHeadAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Winger to Head (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents.totalWingerToHeadAwayGoals /
                        10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalWingerToHeadAwayGoals +
                          simulateResults.specialEvents
                            .totalWingerToHeadAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalTechnicalVsHeadHomeGoals' in simulateResults.specialEvents &&
                'totalTechnicalVsHeadHomeMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Technical vs Head (Home)</td>
                    <td>
                      {(
                        simulateResults.specialEvents
                          .totalTechnicalVsHeadHomeGoals / 10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalTechnicalVsHeadHomeGoals +
                          simulateResults.specialEvents
                            .totalTechnicalVsHeadHomeMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
              {'totalTechnicalVsHeadAwayGoals' in simulateResults.specialEvents &&
                'totalTechnicalVsHeadAwayMisses' in simulateResults.specialEvents && (
                  <tr>
                    <td>Technical vs Head (Away)</td>
                    <td>
                      {(
                        simulateResults.specialEvents
                          .totalTechnicalVsHeadAwayGoals / 10000
                      ).toFixed(2)}{' '}
                      /{' '}
                      {(
                        (simulateResults.specialEvents
                          .totalTechnicalVsHeadAwayGoals +
                          simulateResults.specialEvents
                            .totalTechnicalVsHeadAwayMisses) /
                        10000
                      ).toFixed(2)}
                    </td>
                  </tr>
                )}
            </tbody>
          </Table>
        </div>
      )}
      {breakdown && (
        <div
          style={{
            marginTop: 24,
            border: '1px solid #555',
            borderRadius: 6,
            padding: 12,
            background: '#25282c',
          }}
        >
          <h6>Simulation breakdown</h6>
          <div style={{ overflowX: 'auto' }}>
            <Table striped bordered hover variant="dark" size="sm" style={{ whiteSpace: 'nowrap', marginBottom: 16 }}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Total</th>
                  {standardColumns.map(column => <th key={column.label}>{column.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {['home', 'away'].flatMap(side => [
                  breakdownRow('Chance', side, column => perMatch(column.chances(side))),
                  breakdownRow('Goals', side, column => perMatch(column.goals(side))),
                  breakdownRow('Conv%', side, column => {
                    const chances = column.chances(side)
                    return chances > 0 ? (column.goals(side) / chances).toFixed(2) : '0.00'
                  }),
                ])}
              </tbody>
            </Table>
            {events && (
              <Table striped bordered hover variant="dark" size="sm" style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Special Events Breakdown</th>
                    {specialColumns.map(column => <th key={column.label}>{column.label}</th>)}
                    <th>SE Total</th>
                  </tr>
                </thead>
                <tbody>
                  {['home', 'away'].flatMap(side => {
                    const label = side === 'home' ? 'Home' : 'Away'
                    const values = [
                      ['Chance', column => eventValue(events, side, column.type), specialTotal(side)],
                      ['Goals', column => eventGoals(events, side, column.type), specialGoalTotal(side)],
                    ]
                    return values.map(([type, getter, total]) => (
                      <tr key={`${side}-${type}`}>
                        <td>{type} {label}</td>
                        {specialColumns.map(column => <td key={column.label}>{perMatch(getter(column))}</td>)}
                        <td>{perMatch(total)}</td>
                      </tr>
                    ))
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      )}
      {false && simulateResults.chanceAllocation && (
        <div style={{ marginTop: 10, color: '#ccc', fontSize: 13 }}>
          Chances: Home <b>{perMatch(simulateResults.chanceAllocation.home)}</b>
          {' + '}Away <b>{perMatch(simulateResults.chanceAllocation.away)}</b>
          {' + '}Chance missed <b>{perMatch(simulateResults.chanceAllocation.missed)}</b>
          {' = '}<b>{perMatch(
            simulateResults.chanceAllocation.home +
              simulateResults.chanceAllocation.away +
              simulateResults.chanceAllocation.missed
          )}</b>
        </div>
      )}
    </div>
  )
}

export default Results
